import { useEffect, type ReactNode } from "react";
import { ShieldCheck } from "lucide-react";
import { cn } from "@bindery/ui/lib/utils";
import { MICRO_CAPS } from "./type";

/**
 * The frame every checkout page sits in — a letterhead, a narrow column, and
 * a tear line, in that order.
 *
 * Ported from the monorepo's checkout `layout.tsx`, which was a Next route
 * group; here it is an ordinary component the three routes render, because a
 * client-side router has no layout segment to hang it on.
 *
 * The whole surface is deliberately a DOCUMENT rather than a card floating on
 * a page: full-bleed hairlines top and bottom, a measure narrow enough to read
 * in one glance, and no elevation anywhere. Payment interstitials that reach
 * for a drop-shadowed panel on a gradient all end up looking like the same
 * generic dashboard, and a shopper mid-transaction is being asked to trust
 * this page with a fact about their money — a receipt is the right register
 * for that, and a dashboard is not.
 */
export function CheckoutShell({
  title,
  children,
}: {
  /** The full document title for this route, verbatim. */
  readonly title: string;
  readonly children: ReactNode;
}) {
  // Synchronising an external system (the document) with a prop — the
  // sanctioned use of an effect. index.html ships a static title for the
  // first paint; this replaces it once a route has matched.
  useEffect(() => {
    document.title = title;
  }, [title]);

  return (
    <div className="flex min-h-svh flex-col">
      <header className="border-rule animate-print-in border-b">
        <div className="mx-auto flex w-full max-w-[26rem] items-center justify-between gap-4 px-5 py-5">
          <a
            href="https://sara.ai"
            className="focus-visible:ring-ring/50 inline-flex items-center gap-2.5 rounded-sm outline-none focus-visible:ring-[3px]"
          >
            <span className="font-smcp text-[1.05rem] leading-none tracking-[0.055em] lowercase">
              Sara&rsquo;s Books
            </span>
            <span aria-hidden className="text-rule text-sm leading-none">
              /
            </span>
            <span className={cn(MICRO_CAPS, "text-muted-foreground")}>
              Checkout
            </span>
          </a>

          {/* Not a badge and not a padlock chip — a mark, set in the same
              micro-caps as everything else that labels rather than speaks.
              The word is dropped on narrow screens; the icon carries it. */}
          <span
            className={cn(
              MICRO_CAPS,
              "text-muted-foreground inline-flex items-center gap-1.5",
            )}
          >
            <ShieldCheck className="size-3.5" aria-hidden />
            <span className="hidden sm:inline">Secure</span>
          </span>
        </div>
      </header>

      {/* `m-auto` on the child rather than `justify-center` on the flex
          container: centring by `justify-content` clips the OVERFLOWING end
          of a child taller than its container, which on a short viewport
          would cut the top off the about page with no way to scroll to it.
          Auto margins centre the same way and collapse instead of clipping. */}
      <main className="flex flex-1 flex-col px-5 py-12 sm:py-16">
        <div
          className="animate-print-in m-auto w-full max-w-[26rem]"
          style={{ animationDelay: "70ms" }}
        >
          {children}
        </div>
      </main>

      <footer
        className="animate-print-in"
        style={{ animationDelay: "140ms" }}
        // A perforation rather than a rule: the one piece of skeuomorphism on
        // the page, and the thing that says "receipt" without a paper texture
        // or a zigzag edge.
      >
        <div className="text-rule leader h-[1.5px] w-full" aria-hidden />
        {/* A step smaller and less letterspaced than the rest of the micro
            layer: this line is the longest string on the surface, and at the
            shared tracking it overflowed the measure on a phone and wrapped
            with "LLC" orphaned onto a colliding second line. */}
        <p
          className={cn(
            MICRO_CAPS,
            "text-muted-foreground mx-auto w-full max-w-[26rem] px-5 py-5 text-center text-[0.625rem] leading-[1.7] tracking-[0.11em] text-balance",
          )}
        >
          Payments processed by Stripe &middot; Sara&rsquo;s Books LLC
        </p>
      </footer>
    </div>
  );
}
